import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, CalendarPlus } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");

function parseTime(dateStr, timeStr, fallbackHour = 9) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const match = (timeStr || "").match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i);
  let hh = fallbackHour, mm = 0;
  if (match) {
    hh = +match[1]; mm = +(match[2] || 0);
    const ap = (match[3] || "").toUpperCase();
    if (ap === "PM" && hh < 12) hh += 12;
    if (ap === "AM" && hh === 12) hh = 0;
  }
  return new Date(y, m - 1, d, hh, mm);
}

function AddToCalendar({ title, start, end, location, details }) {
  const fmt = (dt) => `${dt.getFullYear()}${pad(dt.getMonth() + 1)}${pad(dt.getDate())}T${pad(dt.getHours())}${pad(dt.getMinutes())}00`;
  const gcal = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${fmt(start)}/${fmt(end)}&location=${encodeURIComponent(location || "")}&details=${encodeURIComponent(details || "")}`;
  const downloadIcs = () => {
    const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Cake Society//EN", "BEGIN:VEVENT",
      `DTSTART:${fmt(start)}`, `DTEND:${fmt(end)}`, `SUMMARY:${title}`,
      `LOCATION:${location || ""}`, `DESCRIPTION:${details || ""}`, "END:VEVENT", "END:VCALENDAR"].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div data-testid="add-to-calendar" className="mt-6 flex flex-wrap items-center justify-center gap-3">
      <a data-testid="gcal-link" href={gcal} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.2em] uppercase font-bold transition-colors">
        <CalendarPlus size={15} /> Add to Google Calendar
      </a>
      <button data-testid="ics-download" onClick={downloadIcs} className="inline-flex items-center gap-2 px-5 py-3 border-2 border-[#121212] text-[#121212] hover:bg-[#F2E9D8] text-xs tracking-[0.2em] uppercase font-bold transition-colors">
        <CalendarPlus size={15} /> Apple / Outlook (.ics)
      </button>
    </div>
  );
}

function ClassCalendar({ tx }) {
  if (tx?.item_type === "class" && tx?.class?.date) {
    const c = tx.class;
    const start = parseTime(c.date, c.time, 10);
    const hrs = parseFloat((c.duration || "").match(/[\d.]+/)?.[0]) || 2;
    const end = new Date(start.getTime() + hrs * 3600000);
    return <AddToCalendar title={c.title || "Cake Society Class"} start={start} end={end} location={c.location} details={`Your Cake Society class: ${c.title || ""}`} />;
  }
  if (tx?.item_type === "open_studio" && tx?.open_studio?.date) {
    const o = tx.open_studio;
    const [s, e] = (o.time_slot || "").split(" - ");
    const start = parseTime(o.date, s, 12);
    const end = e ? parseTime(o.date, e, 14) : new Date(start.getTime() + 2 * 3600000);
    return <AddToCalendar title="Open Studio · Cake Society" start={start} end={end} location={o.location} details="Your Open Studio table at Cake Society" />;
  }
  return null;
}

function GuestAccountPrompt({ tx }) {
  const { register, user } = useAuth();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (done || (user && user !== false)) {
    return (
      <div data-testid="guest-account-created" className="mt-10 bg-[#C6FF33] border-2 border-[#121212] p-6 text-left">
        <p className="font-serif font-black text-2xl text-[#121212]">Account ready 🎉</p>
        <p className="text-sm text-[#121212]/80 mt-1">Your booking is saved. Head to your dashboard anytime.</p>
        <Link to="/dashboard" data-testid="guest-account-dashboard-link" className="mt-4 inline-block px-6 py-3 bg-[#121212] text-[#F2E9D8] text-xs tracking-[0.2em] uppercase font-bold">My dashboard</Link>
      </div>
    );
  }

  const create = async () => {
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await register({ email: tx.guest_email, password, name: tx.guest_name || "Guest" });
      await api.post("/me/claim-guest");
      setDone(true);
      toast.success("Account created — your booking is saved!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="guest-account-prompt" className="mt-10 bg-white border-2 border-[#121212] p-6 text-left">
      <p className="text-xs tracking-[0.3em] uppercase font-bold">Optional</p>
      <p className="font-serif font-black text-2xl text-[#121212] mt-1">Create an account to track your bookings</p>
      <p className="text-sm text-[#121212]/70 mt-1">Set a password for <strong>{tx.guest_email}</strong> and find all your bookings in one place.</p>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <input
          data-testid="guest-account-password"
          type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="Choose a password"
          className="flex-1 px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none"
        />
        <button
          data-testid="guest-account-create-btn"
          onClick={create} disabled={busy}
          className="px-6 py-3 bg-[#121212] text-[#F2E9D8] hover:bg-black disabled:opacity-40 text-xs tracking-[0.2em] uppercase font-bold whitespace-nowrap"
        >
          {busy ? "Creating…" : "Create account"}
        </button>
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [tx, setTx] = useState(null);

  useEffect(() => {
    if (!sid) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setTx(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.payment_status === "expired" || data.payment_status === "failed") { setStatus("failed"); return; }
        if (attempts++ < 30) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch {
        if (attempts++ < 30) setTimeout(poll, 2000);
        else setStatus("error");
      }
    };
    poll();
  }, [sid]);

  return (
    <div data-testid="payment-success-page" className="pt-40 pb-24 fade-in">
      <div className="max-w-xl mx-auto px-6 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="mx-auto animate-spin text-[#121212]" size={48} />
            <h1 className="mt-6 font-serif text-4xl text-stone-900">Finalizing your order…</h1>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-500" size={64} />
            <h1 className="mt-6 font-serif text-5xl text-stone-900">
              {tx?.item_type === "membership" ? "Welcome to the club!" : tx?.item_type === "open_studio" ? "Studio reserved!" : tx?.item_type === "shop" ? "Order confirmed!" : "Booked!"}
            </h1>
            <p className="mt-4 text-stone-600">
              {tx?.item_type === "membership"
                ? "Your Cake Society membership is active. Enjoy your perks!"
                : tx?.item_type === "shop"
                ? (tx?.fulfillment === "ship" ? "We'll ship your order shortly. A confirmation is on its way to your inbox." : "Your order will be ready for pickup at the studio. A confirmation is on its way to your inbox.")
                : "A confirmation will be in your inbox shortly."}
            </p>
            {tx?.item_type === "shop" && tx?.kit_codes?.length > 0 && (
              <div data-testid="success-kit-codes" className="mt-6 text-left bg-[#121212] text-[#F2E9D8] border-2 border-[#121212] p-6">
                <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C6FF33]">Your Vault access codes</p>
                <p className="text-sm text-[#F2E9D8]/75 mt-1">Open the matching tutorial in the Vault and enter your code to unlock it.</p>
                <div className="mt-4 space-y-3">
                  {tx.kit_codes.map((c, idx) => (
                    <div key={idx} data-testid={`success-kit-code-${idx}`} className="bg-[#F2E9D8] text-[#121212] p-4 text-center">
                      <p className="text-[11px] tracking-[0.2em] uppercase font-bold">{c.tutorial_title}</p>
                      <p className="font-mono font-bold text-2xl tracking-[0.15em] mt-1">{c.code}</p>
                      {c.tutorial_id && (
                        <Link to={`/tutorials/${c.tutorial_id}?kit=1`} className="mt-2 inline-block text-[11px] tracking-[0.2em] uppercase font-bold underline">Unlock tutorial →</Link>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {tx?.tshirts?.length > 0 && (
              <div data-testid="success-tshirt-note" className="mt-6 inline-block bg-[#C6FF33] border-2 border-[#121212] px-5 py-3 text-sm text-[#121212] font-medium">
                👕 T-shirt order confirmed ({tx.tshirts.map((t) => `${t.qty}× ${t.size}`).join(", ")}) — pick it up at the studio on class day.
              </div>
            )}
            {tx?.item_type === "camp_bundle" && tx?.bundle && (
              <div data-testid="success-bundle-camps" className="mt-6 text-left border-2 border-[#121212] bg-[#F5EFE6] p-5">
                <p className="font-serif font-black text-xl text-[#121212]">{tx.bundle.title}</p>
                <ul className="mt-2 space-y-1 text-sm text-[#121212]">
                  {tx.bundle.camps.map((c) => <li key={c.id}>✓ {c.title} — {c.date}{c.time ? ` · ${c.time}` : ""}</li>)}
                </ul>
                {tx.bundle.savings > 0 && <p className="mt-3 text-sm font-bold text-[#E0218A]">You saved ${Number(tx.bundle.savings).toFixed(2)}!</p>}
              </div>
            )}
            <ClassCalendar tx={tx} />
            {tx?.guest_email ? (
              <GuestAccountPrompt tx={tx} />
            ) : (
              <Link
                data-testid="success-dashboard-link"
                to={tx?.item_type === "membership" ? "/membership" : "/dashboard"}
                className="mt-10 inline-block px-8 py-4 rounded-none bg-[#121212] text-white hover:bg-[#000000] transition-all"
              >
                {tx?.item_type === "membership" ? "View my membership" : "Go to my dashboard"}
              </Link>
            )}
          </>
        )}
        {(status === "failed" || status === "timeout" || status === "error") && (
          <>
            <XCircle className="mx-auto text-red-500" size={48} />
            <h1 className="mt-6 font-serif text-4xl text-stone-900">Something went wrong.</h1>
            <p className="mt-4 text-stone-600">Your card may not have been charged. Please try again.</p>
            <Link to="/" className="mt-10 inline-block text-[#121212] hover:underline">Back to home</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page" className="pt-40 pb-24 text-center fade-in">
      <div className="max-w-xl mx-auto px-6">
        <h1 className="font-serif text-5xl text-stone-900">Order cancelled.</h1>
        <p className="mt-4 text-stone-600">No worries — your card hasn't been charged.</p>
        <Link to="/" className="mt-10 inline-block px-8 py-4 rounded-none bg-stone-900 text-white hover:bg-stone-800 transition-all">
          Back to home
        </Link>
      </div>
    </div>
  );
}
